import { getFunctionErrorDetails } from "@/lib/invoke-with-session-retry";

export type AiBudgetOptions = {
  tokenBudget?: number;
  isExamSeason?: boolean;
  estimatedCost?: number;
};

export type AiUsageSnapshot = {
  bucket: "LIGHT_TURN" | "HEAVY_TASK" | "PREMIUM_MODEL";
  remainingCredits?: number;
  percentUsed?: number;
  windowResetAt?: string;
  estimatedOnly?: boolean;
};

type FunctionErrorPayload = {
  error?: string;
  message?: string;
  details?: string;
  usage?: AiUsageSnapshot;
};

export const ensureTokenBudget = (options: AiBudgetOptions | undefined, fallbackCost: number) => {
  void options;
  void fallbackCost;
  // Server-owned quota windows are now authoritative.
};

export const deductBudget = (options: AiBudgetOptions | undefined, fallbackCost: number) => {
  void options;
  void fallbackCost;
  // Keep client budget deduction disabled while rollout finishes.
};

const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
    .join(",")}}`;
};

export const buildAiIdempotencyKey = (seed: Record<string, unknown>) => {
  const source = stableStringify(seed);
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `ai-${(hash >>> 0).toString(16)}-${source.length.toString(16)}`;
};

const formatQuotaReset = (windowResetAt?: string) => {
  if (!windowResetAt) return "";
  const resetDate = new Date(windowResetAt);
  if (Number.isNaN(resetDate.getTime())) return "";
  return ` Resets ${resetDate.toLocaleString()}.`;
};

const humanizeQuotaBucket = (usage?: AiUsageSnapshot) => {
  if (!usage?.bucket) return null;

  if (usage.bucket === "PREMIUM_MODEL") {
    return `Advanced Toofi help is paused for now. Other Toofi tools may still be available.${formatQuotaReset(usage.windowResetAt)}`;
  }

  if (usage.bucket === "HEAVY_TASK") {
    return `Long-form Toofi help is paused for now. Shorter help may still be available.${formatQuotaReset(usage.windowResetAt)}`;
  }

  if (usage.bucket === "LIGHT_TURN") {
    return `Toofi is paused for now in this window.${formatQuotaReset(usage.windowResetAt)}`;
  }

  return null;
};

const humanizeFunctionError = (message: string | null | undefined, payload?: FunctionErrorPayload | null) => {
  if (!message && !payload?.error) return null;
  const normalized = `${message ?? ""} ${payload?.error ?? ""}`.toLowerCase();
  if (normalized.includes("quota_limit_reached")) {
    return humanizeQuotaBucket(payload?.usage) ?? "Toofi is temporarily paused for this request. Other help may still be available.";
  }
  if (normalized.includes("quota_bucket_disabled")) {
    return "This type of Toofi help is not available on your current plan.";
  }
  return message;
};

const getFunctionErrorPayload = async (error: unknown): Promise<FunctionErrorPayload | null> => {
  if (!error || typeof error !== "object" || !("context" in error)) return null;
  const context = (error as { context?: unknown }).context;
  if (!(context instanceof Response)) return null;

  try {
    return (await context.clone().json()) as FunctionErrorPayload;
  } catch {
    return null;
  }
};

export const toFunctionErrorMessage = async (error: unknown, fallback: string) => {
  const details = await getFunctionErrorDetails(error);
  const payload = await getFunctionErrorPayload(error);
  const rawMessage = details ?? (error as { message?: string })?.message ?? fallback;
  return humanizeFunctionError(rawMessage, payload) ?? fallback;
};
